import { useState } from 'react'
import { isEmpty, isEqual, isNil, omit } from 'lodash-es'

import { useUkTimeZone } from '../../../../../hooks'
import {
  getNormalizedParams,
  getSingleFieldParams,
  isApiBadRequestStatus,
} from '../../../../../utils/api'
import {
  getDateOptions,
  getSelectTypeOptions,
} from '../../../../../utils/filter'
import {
  getStaticFilterOptionsByName,
  getStaticFilterSingleFieldResponse,
} from '../../../Filter.utils'
import {
  getDataParams,
  getFilterData,
  hasEmptyFilterValues,
} from '../../FilterBar.utils'

export function useFilterBarLoadData({
  apiUrl,
  availableFilters,
  entity,
  excludeFilters,
  filterValues,
  parsePermanentFilters,
  permanentFilters,
  onClear,
}) {
  const { timezone } = useUkTimeZone()

  const [isInitiated, setInitiated] = useState(false)

  const [filterOptions, setFilterOptions] = useState(null)

  async function getData(item) {
    // A facet with static options answers locally — there is nothing to fetch.
    // TODO: drop this branch once every facet is served by the endpoint.
    const staticOptions =
      getStaticFilterOptionsByName(availableFilters)[item[0]]

    if (staticOptions) {
      return getStaticFilterSingleFieldResponse(staticOptions, item[1])
    }

    return getFilterData({
      ...getDataParams(item),
      apiUrl,
      entity,
      parsePermanentFilters,
      permanentFilters,
    })
  }

  function loadFilterBarData() {
    setInitiated(false)

    const currentFilters = omit(filterValues, excludeFilters)

    if (isEmpty(currentFilters)) {
      if (filterOptions) {
        setFilterOptions(null)
      }

      return Promise.resolve()
    }

    const multiSelectOptions = getSingleFieldParams(currentFilters)
    const selectOptions = getSelectTypeOptions(currentFilters, availableFilters)
    const params = [
      ...getNormalizedParams(selectOptions),
      ...multiSelectOptions,
    ]

    const dateOptions = getDateOptions({
      availableFilters,
      currentFilters,
      timezone,
    })

    return Promise.all(params.map(getData))
      .then(response => {
        const nextOptions = params.reduce(
          (acc, item, index) => {
            acc[item[0]] = !isNil(selectOptions[item[0]])
              ? response[index].data.items[0]
              : response[index].data.items

            return acc
          },
          { ...currentFilters, ...dateOptions }
        )

        if (!isEqual(nextOptions, filterOptions)) {
          setFilterOptions(nextOptions)

          // `return` prevent `setInitiated` and applying filters values
          // so it breaks set valid filters if from API came empty array for single field value
          if (!hasEmptyFilterValues(nextOptions)) {
            return nextOptions
          }

          setInitiated(true)
        }
      })
      .catch(err => {
        if (isApiBadRequestStatus(err.response.status)) {
          onClear()
        }
      })
  }

  return {
    filterOptions,
    handleChangeFilterOptions: setFilterOptions,
    isInitiated,
    loadFilterBarData,
  }
}
